import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigation, useRoute } from '@react-navigation/native';
import { goBackOrHome } from '../../navigation/goBackOrHome';
import { MaterialIcons } from '@expo/vector-icons';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Alert, Pressable, StyleSheet, Text, View } from 'react-native';
import * as yup from 'yup';
import {
  AuthShell,
  CountdownTimer,
  OTPInput,
  PrimaryCTA,
} from '../../components';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { fpColors } from '../../styles/theme';
import {
  autoLoginAfterVerification,
  clearError,
  logoutUser,
  resendVerificationCode,
  selectAuthError,
  selectAuthLoading,
  selectIsVerifying,
  selectUser,
  selectValidationErrors,
  selectVerificationExpiresAt,
  selectVerificationToken,
  verifyEmail,
} from './authSlice';

const schema = yup.object({
  code: yup
    .string()
    .matches(/^\d{6}$/, 'Verification code must be 6 digits')
    .required('Verification code is required'),
});

type VerifyType = 'registration' | 'login' | 'email_change' | 'password_reset';

interface RouteParams {
  type?: VerifyType;
  email?: string;
}

const copy: Record<VerifyType, { title: string; subtitle: (email: string) => string }> = {
  registration: {
    title: 'Verify your email',
    subtitle: (e) =>
      `We've sent a 6-digit verification code to ${e}. Enter it below to activate your account.`,
  },
  login: {
    title: 'Verify your account',
    subtitle: (e) => `Enter the 6-digit code sent to ${e} to continue.`,
  },
  email_change: {
    title: 'Verify new email',
    subtitle: (e) =>
      `We've sent a code to ${e}. Enter it to confirm your new email address.`,
  },
  password_reset: {
    title: 'Confirm reset code',
    subtitle: (e) => `Enter the 6-digit code sent to ${e} to reset your password.`,
  },
};

export const EmailVerificationScreen: React.FC = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const dispatch = useAppDispatch();

  const { type = 'registration', email } = (route.params as RouteParams) || {};

  const isLoading = useAppSelector(selectAuthLoading);
  const isVerifying = useAppSelector(selectIsVerifying);
  const error = useAppSelector(selectAuthError);
  const validationErrors = useAppSelector(selectValidationErrors);
  const verificationToken = useAppSelector(selectVerificationToken);
  const verificationExpiresAt = useAppSelector(selectVerificationExpiresAt);
  const user = useAppSelector(selectUser);

  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<{ code: string }>({
    resolver: yupResolver(schema),
    defaultValues: { code: '' },
  });

  const codeValue = watch('code');

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendCooldown]);

  const remainingSeconds = verificationExpiresAt
    ? Math.max(0, Math.floor((verificationExpiresAt - Date.now()) / 1000))
    : 1800;

  const userEmail = email || user?.email || 'your email';
  const { title, subtitle } = copy[type] ?? copy.registration;

  const onSubmit = async ({ code }: { code: string }) => {
    if (!verificationToken) {
      Alert.alert('Error', 'No verification token found. Please try again.');
      return;
    }
    const result = await dispatch(verifyEmail({ code, token: verificationToken }));

    if (verifyEmail.fulfilled.match(result)) {
      if (type === 'registration' || type === 'login') {
        const auto = await dispatch(autoLoginAfterVerification());
        if (!autoLoginAfterVerification.fulfilled.match(auto)) {
          Alert.alert(
            'Verified',
            'Your email has been verified. Please sign in.',
            [
              {
                text: 'Sign In',
                onPress: () => {
                  dispatch(logoutUser({ callBackend: false }));
                  (navigation as any).navigate('Login');
                },
              },
            ],
          );
        }
      } else if (type === 'password_reset') {
        (navigation as any).navigate('ResetPassword');
      } else if (type === 'email_change') {
        Alert.alert('Success', 'Email changed successfully!', [
          { text: 'Continue', onPress: () => goBackOrHome(navigation) },
        ]);
      }
    } else {
      const msg =
        (result as any).error?.message ||
        'Verification failed. Please check your code and try again.';
      Alert.alert('Verification Failed', msg);
    }
  };

  const handleResend = async () => {
    if (resendLoading || resendCooldown > 0 || !verificationToken) return;
    setResendLoading(true);
    const result = await dispatch(resendVerificationCode(verificationToken));
    if (resendVerificationCode.fulfilled.match(result)) {
      setResendCooldown(60);
      Alert.alert('Code sent', 'A new verification code has been sent to your email.');
    } else {
      Alert.alert('Error', 'Failed to resend code. Please try again.');
    }
    setResendLoading(false);
  };

  const handleCodeChange = (value: string) => {
    setValue('code', value);
    if (value.length === 6 && /^\d{6}$/.test(value)) {
      handleSubmit(onSubmit)();
    }
  };

  const handleStartOver = () => {
    Alert.alert(
      'Start over?',
      'This signs you out so you can register or log in with a different email.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Start Over',
          style: 'destructive',
          onPress: async () => {
            await dispatch(logoutUser({ callBackend: false }));
            (navigation as any).navigate('Login');
          },
        },
      ],
    );
  };

  const errorText = (() => {
    if (typeof error === 'string' && error.length > 0) return error;
    if (validationErrors) {
      if (typeof validationErrors === 'string') return validationErrors;
      if (Array.isArray(validationErrors) && validationErrors.length > 0) {
        const first = validationErrors[0];
        return typeof first === 'string' ? first : (first as any)?.message ?? null;
      }
    }
    return null;
  })();

  return (
    <AuthShell showBack onBack={() => (navigation as any).navigate('Login')}>
      <View style={styles.headingBlock}>
        <View style={styles.iconBadge}>
          <MaterialIcons name="mark-email-read" size={28} color={fpColors.primary} />
        </View>
        <Text style={styles.h1}>{title}</Text>
        <Text style={styles.subtitle}>{subtitle(userEmail)}</Text>
      </View>

      <View style={styles.form}>
        <Controller
          control={control}
          name="code"
          render={({ field: { value } }) => (
            <OTPInput
              value={value}
              onChange={handleCodeChange}
              error={errors.code?.message}
              autoFocus
            />
          )}
        />

        <View style={styles.timerRow}>
          <Text style={styles.timerLabel}>Code expires in </Text>
          <CountdownTimer
            initialSeconds={remainingSeconds}
            format="mm:ss"
            style={styles.timer}
            onExpire={() => {}}
          />
        </View>

        {errorText ? (
          <View style={styles.errorBanner}>
            <MaterialIcons name="error-outline" size={18} color={fpColors.error} />
            <Text style={styles.errorBannerText}>{errorText}</Text>
          </View>
        ) : null}

        <PrimaryCTA
          label="Verify email"
          onPress={handleSubmit(onSubmit)}
          loading={isVerifying || isLoading}
          disabled={codeValue.length !== 6}
        />

        <View style={styles.resendRow}>
          <Text style={styles.resendText}>Didn&apos;t receive the code?</Text>
          <Pressable
            onPress={handleResend}
            disabled={resendLoading || resendCooldown > 0}
            hitSlop={6}
          >
            <Text
              style={[
                styles.resendLink,
                (resendLoading || resendCooldown > 0) && styles.resendLinkDisabled,
              ]}
            >
              {resendLoading
                ? 'Sending…'
                : resendCooldown > 0
                  ? `Resend in ${resendCooldown}s`
                  : 'Resend code'}
            </Text>
          </Pressable>
        </View>
      </View>

      <View style={styles.startOverWrap}>
        <Pressable onPress={handleStartOver} hitSlop={6}>
          <Text style={styles.startOverText}>Start over with a different account</Text>
        </Pressable>
      </View>
    </AuthShell>
  );
};

const styles = StyleSheet.create({
  headingBlock: {
    marginBottom: 16,
    alignItems: 'center',
  },
  iconBadge: {
    width: 44,
    height: 44,
    borderRadius: 14,
    backgroundColor: fpColors.surfaceContainerLow,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  h1: {
    fontFamily: 'Inter_700Bold',
    fontSize: 22,
    color: fpColors.onBackground,
    marginBottom: 4,
    textAlign: 'center',
  },
  subtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    lineHeight: 18,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
  },
  form: {
    gap: 12,
  },
  timerRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  timerLabel: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
  },
  timer: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 13,
    color: fpColors.primary,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 12,
    borderRadius: 10,
    backgroundColor: fpColors.errorContainer,
  },
  errorBannerText: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.onErrorContainer,
  },
  resendRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 6,
    marginTop: 4,
    flexWrap: 'wrap',
  },
  resendText: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
  },
  resendLink: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.primary,
  },
  resendLinkDisabled: {
    color: fpColors.outline,
  },
  startOverWrap: {
    marginTop: 16,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: fpColors.outlineVariant,
    alignItems: 'center',
  },
  startOverText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
  },
});
